import { is } from './is';

/**
 * Determines whether or not two object are shallow-equal; that is, they have
 * all the same keys and each value matches using `===` or a provided comparer.
 *
 * The two objects being compared are expected to have the same prototype, or
 * else the results will not be consistent.
 *
 * This logic is inspired by the implementation by Facebook used in React:
 * https://github.com/facebook/fbjs/blob/master/packages/fbjs/src/core/shallowEqual.js
 *
 * @public
 */
export function areShallowEqual<TObject>(
  left: TObject,
  right: TObject,
  comparer: ((left: TObject[keyof TObject], right: TObject[keyof TObject], key: keyof TObject) => boolean) = is
): boolean {
  return !areShallowDifferent(left, right, { comparer });
}

/**
 * Determines whether or not two objects are shallow-different; that is, they
 * have any key which is unique or which does not satisfy a `===` or provided comparison.
 *
 * If `debug` is set to `true`, then this function outputs the difference determined by
 * the comparison.
 */
export function areShallowDifferent<TObject>(
  left: TObject,
  right: TObject,
  options: {
    comparer?: ((left: TObject[keyof TObject], right: TObject[keyof TObject], key: keyof TObject) => boolean),
    debug?: boolean
  } = {}
): boolean {
  const { comparer = is, debug = false } = options;

  if (is(left, right)) {
    return false;
  }

  if (typeof left !== 'object' ||
    left === null ||
    typeof right !== 'object' ||
    right === null) {
    return debug ? {
      previous: left,
      next: right
    } as {} as boolean : true;
  }

  const leftKeys = Object.keys(left) as (keyof TObject)[];
  const rightKeys = Object.keys(right) as (keyof TObject)[];

  if (leftKeys.length !== rightKeys.length) {
    return debug ? {
      __keys: {
        previous: leftKeys,
        next: rightKeys
      }
    } as {} as boolean : true;
  }

  for (const key of leftKeys) {
    if (!(key in right) ||
      !comparer(left[key], right[key], key)) {
      return debug ? {
        [key]: {
          previous: left[key],
          next: right[key]
        }
      } as {} as boolean : true;
    }
  }

  return false;
}
